import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { AppHeader } from './AppHeader';
import { locationToReturnTo, type ReturnToState } from '../lib/return-to';

/**
 * AuthenticatedRoute — wraps a route's element to redirect unauthenticated
 * users to /signin. For authenticated users, renders the persistent AppHeader
 * (brand mark + staff identity + Cog navigation) above the route's content.
 *
 * Phase 4 [Retake] Goal 3 — AppHeader restored after commit `051a34b` had
 * removed it in favour of "tab strip as nav chrome only" within PR #28.
 *
 * BOW-finesse B6 (30 Jun 2026) — the legacy Phase-3 AdminTabStrip (Home /
 * Onboard placeholder tabs) was deleted. The canonical COG menu in the
 * AppHeader is the sole entry point for every admin surface; the strip was
 * already hidden on /admin/* and its remaining Home/Onboard tabs were dead
 * chrome (the Onboard tab pointed at the removed Phase-3b /onboard wizard,
 * superseded by the in-place SOW/POW/BOW modal launchers).
 */
export function AuthenticatedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  if (status === 'loading') return <div className="portal-loading">Loading…</div>;
  // ⛔ THE REQUESTED PATH TRAVELS WITH THE BOUNCE (UAT fail 1b). This used to be
  // a bare `<Navigate to="/signin" replace />`, which threw the destination away
  // — so every deep link opened without a live session finished on the dashboard.
  // Amplify keeps staff tokens in per-TAB sessionStorage, so "without a live
  // session" is the ordinary case for a link opened in a new tab, which is how
  // bookmarked and shared links are opened. See lib/return-to.ts.
  if (status === 'unauthenticated') {
    const state: ReturnToState = { returnTo: locationToReturnTo(location) };
    return <Navigate to="/signin" replace state={state} />;
  }
  return (
    <>
      <AppHeader />
      {children}
    </>
  );
}
